import React, { useMemo, useState } from 'react';
import { ArrowLeftIcon, Building2Icon, CalendarDaysIcon, CreditCardIcon, HistoryIcon, MapPinIcon, UserCheckIcon, UserMinusIcon, UserPlusIcon } from 'lucide-react';
import { StatusBadge } from './StatusBadge';
import { Customer, LifecycleEvent, Payment, seededLifecycleEvents, UserStatus } from '../data/demoData';
import { formatPrice } from '../data/pricingPlans';
import { useScreenInit } from '../useScreenInit';
type DetailTab = 'details' | 'payments' | 'history' | 'actions';
type UserDetailsProps = {
  user: Customer;
  payments: Payment[];
  onBack: () => void;
  onStatusChange: (status: UserStatus) => void;
};
type TabItem = {
  id: DetailTab;
  label: string;
};
const tabs: TabItem[] = [{
  id: 'details',
  label: 'Details'
}, {
  id: 'payments',
  label: 'Payments'
}, {
  id: 'history',
  label: 'History'
}, {
  id: 'actions',
  label: 'Actions'
}];
export function UserDetails({
  user,
  payments,
  onBack,
  onStatusChange
}: UserDetailsProps) {
  const screenInit = useScreenInit() as {
    tab?: DetailTab;
  };
  const [activeTab, setActiveTab] = useState<DetailTab>(() => isDetailTab(screenInit.tab) ? screenInit.tab : 'details');
  const history = useMemo(() => getLifecycleHistory(user, payments), [payments, user]);
  function handleTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, currentTab: DetailTab) {
    const currentIndex = tabs.findIndex(tab => tab.id === currentTab);
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % tabs.length;
    }
    if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    }
    if (event.key === 'Home') {
      nextIndex = 0;
    }
    if (event.key === 'End') {
      nextIndex = tabs.length - 1;
    }
    if (nextIndex === null) {
      return;
    }
    event.preventDefault();
    const nextTab = tabs[nextIndex];
    setActiveTab(nextTab.id);
    document.getElementById(`user-tab-${nextTab.id}`)?.focus();
  }
  return <section aria-labelledby="user-details-title" data-id="element-382" data-mp="19xi2mlg7txab">
      <button type="button" onClick={onBack} className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#5B5299] hover:text-[#041F60] focus:outline-none focus:underline" data-id="element-383" data-mp="19vx9n09jumek">
        <ArrowLeftIcon size={16} aria-hidden="true" data-id="element-384" data-mp="1onfxfk8nhl8h" /> Back to users
      </button>

      <header className="rounded-xl border border-[#A6B6F8]/50 bg-white p-5 sm:p-6" data-id="element-385" data-mp="1opipsax9zqgd">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-386" data-mp="1squduc0i43mg">
          <div className="min-w-0" data-id="element-387" data-mp="1xlrm3gpyi8ya">
            <h1 id="user-details-title" className="truncate text-3xl font-bold tracking-tight text-[#041F60]" data-id="element-388" data-mp="22mp5txqm7b1w">
              {user.company?.name || user.tradingName}
            </h1>
            <p className="mt-1 truncate text-sm text-stone-600" data-id="element-389" data-mp="89z8t74y9lqa">
              {user.firstName} {user.lastName} · {user.email}
            </p>
          </div>
          <div className="shrink-0" data-id="element-390" data-mp="3s42qx1wsmr7c">
            <StatusBadge status={user.status} size="xlarge" data-id="element-391" data-mp="3s31g8yjow6q5" />
          </div>
        </div>
      </header>

      <div className="mt-5 border-b border-[#A6B6F8]/50" data-id="element-392" data-mp="3n6188i3a4twe">
        <div role="tablist" aria-label="Business record sections" className="flex gap-5 overflow-x-auto" data-id="element-393" data-mp="3i5wtoxlxm98t">
          {tabs.map(tab => {
          const isActive = activeTab === tab.id;
          return <button key={tab.id} id={`user-tab-${tab.id}`} type="button" role="tab" tabIndex={isActive ? 0 : -1} aria-selected={isActive} aria-controls={`user-panel-${tab.id}`} onClick={() => setActiveTab(tab.id)} onKeyDown={event => handleTabKeyDown(event, tab.id)} className={`shrink-0 border-b-2 px-1 pb-3 text-sm font-bold outline-none transition ${isActive ? 'border-[#041F60] text-[#041F60]' : 'border-transparent text-stone-500 hover:border-[#A6B6F8] hover:text-[#041F60]'} focus:ring-2 focus:ring-[#4BE4FF] focus:ring-offset-2`} data-id="element-394" data-mp="eppnajc4txwc">
                {tab.label}
              </button>;
        })}
        </div>
      </div>

      {activeTab === 'details' && <section id="user-panel-details" role="tabpanel" aria-labelledby="user-tab-details" className="mt-5 rounded-xl border border-[#A6B6F8]/50 bg-white p-5 sm:p-6" data-id="element-395" data-mp="10ws5c5eleio5">
          <div className="flex items-center gap-2" data-id="element-396" data-mp="qz45j124qkz4">
            <Building2Icon size={18} className="text-[#5B5299]" aria-hidden="true" data-id="element-397" data-mp="r0fedfafmix3" />
            <h2 className="text-base font-bold text-[#041F60]" data-id="element-398" data-mp="3bo1y4tv4lle7">
              Business details
            </h2>
          </div>
          <div className="mt-5 grid gap-x-8 gap-y-6 text-sm sm:grid-cols-2 lg:grid-cols-3" data-id="element-399" data-mp="3gofxzomkpp15">
            <RecordItem label="Business type" value={user.businessType} data-id="element-400" data-mp="1yoqeiyqzj5x1" />
            <RecordItem label="Business address" value={user.address} icon={<MapPinIcon size={16} aria-hidden="true" data-id="element-402" data-mp="27qzn3j2p0p7s" />} data-id="element-401" data-mp="1trggwvtfzny4" />
            {user.company && <RecordItem label="Company number" value={user.company.number} data-id="element-403" data-mp="23ouwyubgeapw" />}
            {user.company && <RecordItem label="Incorporated" value={user.company.incorporated} icon={<CalendarDaysIcon size={16} aria-hidden="true" data-id="element-405" data-mp="1ayckx3sr4z0c" />} data-id="element-404" data-mp="1g01vkic2banw" />}
            {user.company && <RecordItem label="Companies House" value={user.company.status} data-id="element-406" data-mp="1k1dqn3dpgo3h" />}
            {user.company && <RecordItem label="SIC codes" value={user.company.sicCodes.join(' · ')} wide data-id="element-407" data-mp="warkxir6bfwn" />}
            {user.trialExpiry && <RecordItem label="Trial expiry" value={user.trialExpiry} icon={<CalendarDaysIcon size={16} aria-hidden="true" data-id="element-409" data-mp="2wchjm8iyrsaj" />} data-id="element-408" data-mp="2rdo8gmr5gddj" />}
            <RecordItem label="Plan" value={`${user.planName} · ${formatPrice(user.planPrice)} / month`} data-id="element-410" data-mp="315txpoq0ogp1" />
            {user.planDiscount && <RecordItem label="Discount applied" value={user.planDiscount} data-id="element-411" data-mp="28gmwghz6hd03" />}
            <RecordItem label="Subscription" value={user.subscription} data-id="element-412" data-mp="2de4z5qmlqvzn" />
          </div>
        </section>}

      {activeTab === 'payments' && <section id="user-panel-payments" role="tabpanel" aria-labelledby="user-tab-payments" className="mt-5 overflow-hidden rounded-xl border border-[#A6B6F8]/50 bg-white" data-id="element-413" data-mp="2ig45989q870a">
          <div className="flex items-center justify-between border-b border-[#A6B6F8]/50 px-5 py-4" data-id="element-414" data-mp="2mmzps0jhk6u">
            <h2 className="flex items-center gap-2 text-base font-bold" data-id="element-415" data-mp="1n4o46t6fwpi5">
              <CreditCardIcon size={18} className="text-[#5B5299]" aria-hidden="true" data-id="element-416" data-mp="1n70ld44divu5" />
              Payments
            </h2>
            <span className="text-xs text-stone-500" data-id="element-417" data-mp="1dd9trqcya5a8">GoCardless</span>
          </div>
          {payments.length ? <div className="divide-y divide-stone-100" data-id="element-418" data-mp="18e70122f7lhk">
              {payments.map(payment => <div key={payment.id} className="flex items-center justify-between gap-4 px-5 py-4" data-id="element-419" data-mp="zfcxyw6bm2wm">
                  <div data-id="element-420" data-mp="uh1ownnunwfg">
                    <p className="text-sm font-semibold" data-id="element-421" data-mp="uicsamddv261">{payment.date}</p>
                    <p className="mt-0.5 font-mono text-xs text-stone-500" data-id="element-422" data-mp="pigw39axgf11">
                      {payment.id}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3 sm:gap-5" data-id="element-423" data-mp="2jvgaiz3kn36q">
                    <span className="text-sm font-bold" data-id="element-424" data-mp="2jtdmulpw5m56">
                      £{payment.amount.toFixed(2)}
                    </span>
                    <StatusBadge status={payment.status} data-id="element-425" data-mp="1xp70bp8isu4z" />
                  </div>
                </div>)}
            </div> : <div className="px-5 py-12 text-center" data-id="element-426" data-mp="1ixyfdtfkbwy6">
              <p className="text-sm font-semibold text-[#041F60]" data-id="element-427" data-mp="1ny2lqqmrbbao">
                No payments recorded yet.
              </p>
              <p className="mt-1 text-sm text-stone-500" data-id="element-428" data-mp="19xrxlvcu0at7">
                Payment activity will appear here when available.
              </p>
            </div>}
        </section>}

      {activeTab === 'history' && <section id="user-panel-history" role="tabpanel" aria-labelledby="user-tab-history" className="mt-5 rounded-xl border border-[#A6B6F8]/50 bg-white p-5 sm:p-6" data-id="element-429" data-mp="1509vantbktu6">
          <div className="flex items-start gap-3 border-b border-[#A6B6F8]/50 pb-5" data-id="element-430" data-mp="2zesykslor1d9">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#A6B6F8]/20 text-[#5B5299]" data-id="element-431" data-mp="nqxl487gacw8">
              <HistoryIcon size={18} aria-hidden="true" data-id="element-432" data-mp="iscsatuj2vht" />
            </span>
            <div data-id="element-433" data-mp="xgqssy4h27v7">
              <h2 className="text-base font-bold text-[#041F60]" data-id="element-434" data-mp="xh8zkoorg4hd">
                User history
              </h2>
              <p className="mt-1 text-sm leading-5 text-stone-600" data-id="element-435" data-mp="spquppjm9jr4">
                Key account and subscription milestones.
              </p>
            </div>
          </div>

          <ol className="mt-6 space-y-0" aria-label="User lifecycle history" data-id="element-436" data-mp="12g3q6csyyxc1">
            {history.map((event, index) => {
          const Icon = event.icon;
          const isLast = index === history.length - 1;
          return <li key={event.id} className="relative flex gap-4 pb-7 last:pb-0" data-id="element-437" data-mp="1bh1me8x620k2" data-mpi={index}>
                  {!isLast && <span className="absolute left-[17px] top-9 h-[calc(100%-1.25rem)] w-px bg-[#A6B6F8]/50" aria-hidden="true" data-id="element-438" data-mp="3d8oi2ns06jmv" />}
                  <span className={`relative z-10 grid h-9 w-9 shrink-0 place-items-center rounded-full ${event.tone}`} aria-hidden="true" data-id="element-439" data-mp="2ibsfto7zhrqi">
                    <Icon size={17} data-id="element-440" data-mp="29d6vbniyc2n3" />
                  </span>
                  <div className="min-w-0 pt-0.5" data-id="element-441" data-mp="3624udhxqimq2">
                    <p className="text-sm font-bold text-[#041F60]" data-id="element-442" data-mp="361wh3oxp9fec">
                      {event.title}
                    </p>
                    <p className="mt-1 text-sm leading-5 text-stone-600" data-id="element-443" data-mp="315nuxrl34lxo">
                      {event.description}
                    </p>
                    <p className="mt-1.5 text-xs font-semibold text-stone-500" data-id="element-444" data-mp="2sbsz5a5us7up">
                      {event.date}
                    </p>
                  </div>
                </li>;
        })}
          </ol>
        </section>}

      {activeTab === 'actions' && <section id="user-panel-actions" role="tabpanel" aria-labelledby="user-tab-actions" className="mt-5 rounded-xl border border-[#A6B6F8]/50 bg-white p-5 sm:p-6" data-id="element-445" data-mp="16k2r2ppffg1i">
          <div className="border-b border-[#A6B6F8]/50 pb-5" data-id="element-446" data-mp="1fj6c3z85m5in">
            <h2 className="text-base font-bold text-[#041F60]" data-id="element-447" data-mp="1kjavbx0g5eck">
              Satago actions
            </h2>
            <p className="mt-1 text-sm text-stone-600" data-id="element-448" data-mp="1pinjqm6yuth9">
              Manage this user&apos;s Satago setup and account access.
            </p>
          </div>

          <div className="mt-5 grid gap-4 lg:grid-cols-2" data-id="element-449" data-mp="1ufnjygh4x20g">
            <article className="rounded-xl border border-[#A6B6F8]/60 bg-[#A6B6F8]/15 p-4" data-id="element-450" data-mp="1z7ypbyun9g0e">
              <div className="flex items-start gap-3" data-id="element-451" data-mp="23a1zmk5kuek3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#4BE4FF] text-[#041F60]" data-id="element-452" data-mp="23bmuybzpu1j0">
                  <UserPlusIcon size={18} aria-hidden="true" data-id="element-453" data-mp="2d7zp1az6ahea" />
                </span>
                <div data-id="element-454" data-mp="2d5n7gyxbu91r">
                  <h3 className="font-bold text-[#041F60]" data-id="element-455" data-mp="d5gvahhsd3y3">
                    Set up user in Satago
                  </h3>
                  <p className="mt-1 text-sm leading-5 text-stone-600" data-id="element-456" data-mp="87ov706zmbit">
                    Set up the Satago account and mark the user as active.
                  </p>
                </div>
              </div>
              <button type="button" onClick={() => onStatusChange('Active')} disabled={user.status === 'Active'} className="mt-5 inline-flex w-full items-center justify-center rounded-lg bg-[#041F60] px-4 py-2.5 text-sm font-bold text-white transition enabled:hover:bg-[#031849] disabled:cursor-not-allowed disabled:bg-stone-300 focus:outline-none focus:ring-2 focus:ring-[#4BE4FF] focus:ring-offset-2" data-id="element-457" data-mp="i41mjrf28dae">
                {user.status === 'Active' ? 'User is set up' : 'Set up in Satago'}
              </button>
            </article>

            <article className="rounded-xl border border-rose-200 bg-rose-50 p-4" data-id="element-458" data-mp="1i4n9cp5zxz45">
              <div className="flex items-start gap-3" data-id="element-459" data-mp="1i6q6ru9hauiw">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-rose-100 text-rose-700" data-id="element-460" data-mp="1n2oq33fy9mcg">
                  <UserMinusIcon size={18} aria-hidden="true" data-id="element-461" data-mp="18e2ad0meuskp" />
                </span>
                <div data-id="element-462" data-mp="1dcvh8a76zlbw">
                  <h3 className="font-bold text-[#041F60]" data-id="element-463" data-mp="1dcc5xcc1rv6u">
                    Remove user from Satago
                  </h3>
                  <p className="mt-1 text-sm leading-5 text-stone-600" data-id="element-464" data-mp="zgi9hssgmyam">
                    Remove the Satago account and cancel this account.
                  </p>
                </div>
              </div>
              <button type="button" onClick={() => onStatusChange('Cancelled')} disabled={user.status === 'Cancelled'} className="mt-5 inline-flex w-full items-center justify-center rounded-lg border border-rose-300 bg-white px-4 py-2.5 text-sm font-bold text-rose-700 transition enabled:hover:bg-rose-100 disabled:cursor-not-allowed disabled:border-stone-200 disabled:text-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-200 focus:ring-offset-2" data-id="element-465" data-mp="uf2p82y3wvta">
                {user.status === 'Cancelled' ? 'User has been removed' : 'Remove from Satago'}
              </button>
            </article>
          </div>
        </section>}
    </section>;
}
type RecordItemProps = {
  label: string;
  value: string;
  icon?: React.ReactNode;
  wide?: boolean;
};
function RecordItem({
  label,
  value,
  icon,
  wide
}: RecordItemProps) {
  return <div className={wide ? 'sm:col-span-2 lg:col-span-3' : undefined} data-id="element-466" data-mp="3t5u6olnm1cyl">
      <p className="text-xs font-bold uppercase tracking-[0.12em] text-stone-500" data-id="element-467" data-mp="3o6hfxptmblqq">
        {label}
      </p>
      <p className="mt-1.5 flex items-start gap-2 leading-5 text-[#041F60]" data-id="element-468" data-mp="3j74o0qnw3oha">
        {icon && <span className="mt-0.5 shrink-0 text-[#5B5299]" data-id="element-469" data-mp="3e79wckwaiuv6">{icon}</span>}
        <span data-id="element-470" data-mp="3ea4hken4ojpn">{value}</span>
      </p>
    </div>;
}
type HistoryItem = {
  id: string;
  date: string;
  title: string;
  description: string;
  icon: typeof UserPlusIcon;
  tone: string;
};
function getLifecycleHistory(user: Customer, payments: Payment[]): HistoryItem[] {
  const lifecycleEvents = seededLifecycleEvents.filter(event => event.userId === user.id);
  const firstPayment = payments.reduce<Payment | undefined>((earliest, payment) => {
    if (!earliest || toTimestamp(payment.date) < toTimestamp(earliest.date)) {
      return payment;
    }
    return earliest;
  }, undefined);
  const events = lifecycleEvents.map(historyItemForLifecycleEvent);
  if (firstPayment) {
    events.push({
      id: `first-payment-${firstPayment.id}`,
      date: firstPayment.date,
      title: 'First payment made',
      description: `First collection of £${firstPayment.amount.toFixed(2)} recorded via GoCardless.`,
      icon: CreditCardIcon,
      tone: 'bg-emerald-50 text-emerald-700'
    });
  }
  return events.sort((first, second) => toTimestamp(first.date) - toTimestamp(second.date));
}
function historyItemForLifecycleEvent(event: LifecycleEvent): HistoryItem {
  const historyByKind: Record<LifecycleEvent['kind'], HistoryItem> = {
    signed_up: {
      id: event.id,
      date: event.date,
      title: 'Signed up',
      description: 'Completed the initial Credit Manager account setup.',
      icon: UserPlusIcon,
      tone: 'bg-[#A6B6F8]/20 text-[#5B5299]'
    },
    satago_added: {
      id: event.id,
      date: event.date,
      title: 'Added to Satago',
      description: 'Satago setup was completed for this account.',
      icon: UserCheckIcon,
      tone: 'bg-blue-50 text-blue-700'
    },
    trial_expired: {
      id: event.id,
      date: event.date,
      title: 'Free trial expired',
      description: 'The free trial period ended without a paid subscription.',
      icon: CalendarDaysIcon,
      tone: 'bg-amber-50 text-amber-800'
    },
    cancelled: {
      id: event.id,
      date: event.date,
      title: 'Account cancelled',
      description: 'The account was removed from Satago and marked as cancelled.',
      icon: UserMinusIcon,
      tone: 'bg-rose-50 text-rose-700'
    }
  };
  return historyByKind[event.kind];
}
function toTimestamp(date: string) {
  const timestamp = new Date(date).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}
function isDetailTab(tab: unknown): tab is DetailTab {
  return tab === 'details' || tab === 'payments' || tab === 'history' || tab === 'actions';
}